import './index.less';

import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactSortable, Sortable } from 'react-sortablejs';
import { AddIcon, DeleteIcon, DragMoveIcon } from 'tdesign-icons-react';
import { Button, Form } from 'tdesign-react';

import { IFormItemProps } from '@/components/form/interface';
import { IReplaceMsgTextInfo } from '@/store/modules/__endpoints__/module';

import styles from './index.module.less';
import ItemComp from './Item';

const { FormItem } = Form;

const random = () => `array_${dayjs().valueOf()}`;

interface IFormArrayProps {
  fieldsConfig: IFormItemProps[];
  form: any;
  repeatConfig?: {
    [key: string]: IFormItemProps[]
  };
}

export const CustomArrayReplaceMsg: React.FC<
{
  value: IReplaceMsgTextInfo[];
  onChange: (value: IReplaceMsgTextInfo[]) => void;
  drag: boolean;
  buttonSize: 'small' | 'normal'
  disabled?: boolean;
} & IFormArrayProps
> = (props) => {
  const {
    drag = false,
    value = [],
    onChange,
    buttonSize = 'small',
    disabled = false,
  } = props;

  const defaultValue: IReplaceMsgTextInfo[] = [{ old: '', new: '', type: 2 }];
  const ref = useRef<any[]>([]);

  const [compValue, setCompValue] = useState<IReplaceMsgTextInfo[]>(value.length > 0 ? [] : defaultValue);

  const [keys, setKeys] = useState([defaultValue]?.map(() => random()));
  const [tempKeys, setTempKeys] = useState([defaultValue]?.map(() => random()));

  const [changeKeyIndex, setChangeKeyIndex] = useState<number>(-1);

  useEffect(() => {
    if (value.length > 0) {
      setCompValue([...value]);
      ref.current = [...value];
      setArrayKeys();
    }
  }, [value]);

  useEffect(() => {
    setTempKeys(keys);
  }, [keys]);

  const setArrayKeys = () => {
    if (keys?.length !== value?.length) {
      const newKeys = [...value]?.map((_e, i) => {
        if (i === changeKeyIndex) {
          return `${random()}_${i}`;
        }
        return tempKeys?.[i] || `${random()}_${i}`;
      });
      setKeys(newKeys);
    }
  };

  const removeItem = (
    index: number,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    const newArray = [...compValue];
    newArray.splice(index, 1);
    onChange([...newArray]);
    const tempKeys = [...keys];
    tempKeys.splice(index, 1);
    setKeys(tempKeys);
    if (newArray?.length === 0) {
      setCompValue([]);
    }
  };

  const addItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const newArray = [...compValue];
    newArray.splice(index + 1, 0, ...defaultValue);
    onChange(newArray);

    const tempKeys = [...keys];
    tempKeys.splice(index + 1, 0, random());
    setChangeKeyIndex(index + 1);
    setTempKeys(tempKeys);
  };

  const onEnd = (evt: Sortable.SortableEvent) => {
    const oldIndex = evt.oldIndex || 0;
    const newIndex = evt.newIndex || 0;
    sortKey(oldIndex, newIndex);
    sortData(oldIndex, newIndex);
  };

  const sortData = (oldIndex: number, newIndex: number) => {
    const copyValue = [...compValue];
    const copyValue2 = [...compValue];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);
    onChange(copyValue);
  };

  const sortKey = (oldIndex: number, newIndex: number) => {
    const copyValue = [...tempKeys];
    const copyValue2 = [...tempKeys];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);

    setKeys(copyValue);
  };

  const changeArrayInfoObject = useCallback((key: number, value: IReplaceMsgTextInfo) => {
    const temp = [...ref.current];
    temp[key] = {
      ...temp[key],
      ...value,
    };
    onChange([...temp]);
  }, [compValue]);

  const handleChange = useCallback((value: IReplaceMsgTextInfo, index: number) => {
    changeArrayInfoObject(index, value);
  }, [changeArrayInfoObject]);

  return (
    <div className={styles.arrayWrap}>
      <ReactSortable
        disabled={!drag}
        handle={'.draggable'}
        list={compValue.map((x: any) => ({ ...x }))}
        onEnd={onEnd}
        setList={() => { }}
      >
        {compValue.map(({ }, index) => (
          <div className="custom-form-group" style={{ alignItems: 'flex-end' }} key={`${keys?.[index]}`}>
            <div className={styles.group} style={{ flexGrow: '1' }}>
              <ItemComp value={{ ...ref.current[index] }} disabled={disabled} onChange={(val) => {
                handleChange(val, index);
              }} />
            </div>
            <FormItem className={styles.options}>
              <Button
                className={styles.delete}
                theme="default"
                disabled={compValue.length === 1 || disabled}
                size={buttonSize === 'small' ? 'small' : 'medium'}
                onClick={event => removeItem(index, event)}
                icon={<DeleteIcon />}
                style={{ padding: buttonSize === 'small' ? '0 9px' : undefined }}
              >{buttonSize === 'normal' && '删除'}</Button>
              <Button
                className={styles.add}
                theme="default"
                size={buttonSize === 'small' ? 'small' : 'medium'}
                onClick={event => addItem(index, event)}
                disabled={disabled}
                style={{ padding: buttonSize === 'small' ? '0 8px' : undefined }}
                icon={<AddIcon />}
              >{buttonSize === 'normal' && '增加'}</Button>
              {drag && <Button
                className={classNames('draggable', styles.drag)}
                theme="default"
                size={buttonSize === 'small' ? 'small' : 'medium'}
                icon={<DragMoveIcon />}
              ></Button>}
            </FormItem>
          </div>
        ))}
      </ReactSortable>
    </div>
  );
};
